import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Clock, Sparkles } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSocialProofEnabled } from "@/hooks/useSocialProofEnabled";

const treatments = [
  "Botox", "Lip Filler", "Lip Flip", "Dermal Fillers", "Cheek Filler",
  "Jawline Filler", "Under Eye Filler", "Chin Filler", "Forehead Botox",
  "Crow's Feet Botox", "The Big 3", "Full Upper Face", "Micro-Needling",
  "PRP Therapy", "Sculptra", "Nefertiti Lift", "MicroTox", "TMJ Treatment",
  "Smile Lift", "Bunny Lines Botox", "Baby Botox", "Brow Lift",
  "Masseter Slimming", "Neck Tox", "Lip Enhancement",
  "Free Consultation", "Skin Analysis Session",
  // Spring Flowers Event Promos
  "Spring Bloom - The Big 3", "Spring Bloom - Full Upper Face",
  "Spring Refresh - Lip Filler + Free Lip Flip",
  "Spring Glow - 2 Syringes + Free Tox",
  "Spring Radiance - Micro-Needling Package",
  "Spring Bloom - Forehead + 11's", "Spring Refresh - Cheek Filler",
  "Spring Glow - Sculptra Promo", "Spring Bloom - Nefertiti Lift"
];

const femaleNames = [
  "Sarah", "Marie", "Jessica", "Emma", "Sophie", "Olivia", "Charlotte",
  "Amelie", "Chloe", "Isabella", "Nathalie", "Lauren", "Catherine",
  "Megan", "Nicole", "Rachel", "Danielle", "Michelle", "Amanda", "Jennifer",
  "Stephanie", "Lisa", "Valerie", "Natasha", "Kira", "Samantha", "Emily",
  "Christine", "Julie", "Isabelle", "Audrey", "Genevieve", "Camille"
];

const maleNames = [
  "James", "David", "Michael", "Daniel", "Marc", "Patrick", "Alexandre",
  "Philippe", "Eric", "Thomas", "Mathieu", "Robert"
];

const locations = ["Ottawa", "Kanata", "Carleton Place"];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const generateFreshBooking = (usedTreatments, lastLocation) => {
  // Avoid repeating the same treatment back-to-back
  let treatment;
  do {
    treatment = pick(treatments);
  } while (usedTreatments.includes(treatment) && usedTreatments.length < treatments.length);

  // Avoid repeating the same location back-to-back
  let location;
  do {
    location = pick(locations);
  } while (location === lastLocation && locations.length > 1);

  return {
    name: Math.random() < 0.10 ? pick(maleNames) : pick(femaleNames),
    location,
    treatment,
    timeAgo: Math.random() < 0.3
      ? 1
      : Math.floor(Math.random() * 18) + 2
  };
};

const SocialProofNotification = () => {
  const { language } = useLanguage();
  const socialProofEnabled = useSocialProofEnabled();
  const [currentBooking, setCurrentBooking] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const recentTreatmentsRef = useRef([]);
  const lastLocationRef = useRef(null);
  const timeoutRef = useRef(null);

  const showNext = () => {
    const booking = generateFreshBooking(recentTreatmentsRef.current, lastLocationRef.current);
    lastLocationRef.current = booking.location;

    // Track last 5 treatments to avoid repeats
    recentTreatmentsRef.current = [
      booking.treatment,
      ...recentTreatmentsRef.current.slice(0, 4)
    ];

    setCurrentBooking(booking);
    setIsVisible(true);

    // Auto-hide after 4-7 seconds (random)
    const hideDelay = 4000 + Math.random() * 3000;
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);

      // VERY RANDOM gap: between 25 seconds and 2.5 minutes
      // Weighted: 60% chance short (25-60s), 30% medium (60-120s), 10% long (120-150s)
      let nextDelay;
      const roll = Math.random();
      if (roll < 0.6) {
        // Short gap: 25-60 seconds
        nextDelay = 25000 + Math.random() * 35000;
      } else if (roll < 0.9) {
        // Medium gap: 60-120 seconds (1-2 minutes)
        nextDelay = 60000 + Math.random() * 60000;
      } else {
        // Long gap: 120-150 seconds (2-2.5 minutes)
        nextDelay = 120000 + Math.random() * 30000;
      }
      
      timeoutRef.current = setTimeout(showNext, nextDelay);
    }, hideDelay);
  };

  useEffect(() => {
    // Respect the admin "Social Proof Widget" automation toggle. When OFF
    // (default), do not schedule any popups at all.
    if (socialProofEnabled !== true) return undefined;
    // First notification after 8-20 seconds (random start)
    const initialDelay = 8000 + Math.random() * 12000;
    timeoutRef.current = setTimeout(showNext, initialDelay);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [socialProofEnabled]);

  const formatTimeAgo = (minutes) => {
    if (minutes <= 1) {
      return language === "fr" ? "a l'instant" : "just now";
    }
    if (language === "fr") {
      return `il y a ${minutes} min`;
    }
    return `${minutes} min ago`;
  };

  // Hard gate: even if the toggle flips OFF mid-session, don't render
  // anything. This also covers the default OFF state.
  if (socialProofEnabled !== true) return null;

  return (
    <AnimatePresence>
      {isVisible && currentBooking && (
        <motion.div
          initial={{ x: -400, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -400, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="fixed top-24 md:top-28 left-4 z-30 max-w-[240px] md:max-w-[260px]"
          data-testid="social-proof-notification"
        >
          <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-lg border border-white/30 p-2.5 md:p-3 flex items-start gap-2">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-amber-400/80 to-amber-600/80 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xs md:text-sm">
                {currentBooking.name.charAt(0)}
              </span>
            </div>
            
            <div className="flex-1 min-w-0">
              <p className="text-gray-700 text-[11px] md:text-xs font-medium leading-tight">
                <span className="font-semibold">{currentBooking.name}</span>
                {" "}
                {language === "fr" ? "vient de reserver" : "just booked"}
                {" "}
                <span className="text-amber-700 font-medium">{currentBooking.treatment}</span>
              </p>
              
              <div className="flex items-center gap-2 mt-1 text-[10px] md:text-xs text-gray-500/80">
                <span className="flex items-center gap-0.5">
                  <MapPin className="w-2.5 h-2.5" />
                  {currentBooking.location}
                </span>
                <span className="flex items-center gap-0.5">
                  <Clock className="w-2.5 h-2.5" />
                  {formatTimeAgo(currentBooking.timeAgo)}
                </span>
              </div>
            </div>

            <Sparkles className="w-3 h-3 text-amber-500/70 flex-shrink-0 animate-pulse" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SocialProofNotification;
